import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import ReactCalendar, { CalendarProps } from 'react-calendar';
import Icon from '~ComponentsImproved/Icon';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import dateIcon from '~Assets/icons/calendar_month.svg';
import { DarkCalendar } from '~Styles/calendar';
import { DarkInputStyling, BaseInputStyling, BlueButton, Box, Button } from '~Styles';
import useI18n from '~Translations';
import { useButton } from '~Hooks';
import { DateTime } from 'luxon';

const DateNavigationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  width: 100%;
`;

const DateInputWrapper = styled.div<{ activeDate: boolean; isDark: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 0 16px;
  flex: 1;

  span {
    color: ${({ activeDate, isDark }) => {
      if (isDark) {
        return activeDate ? 'var(--colorWhite)' : 'var(--colorBlueGray200)';
      }
      return activeDate ? 'var(--colorBlueGray1300)' : 'var(--colorBlueGray500)';
    }};
    /** Match the dark input placeholder font (see BaseInputStyling/DarkInputStyling) */
    font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
    font-size: 14px;
  }

  ${({ isDark }) =>
    isDark
      ? css`
          ${BaseInputStyling}
          ${DarkInputStyling}
        `
      : css`
          ${BaseInputStyling}
          background-color: var(--colorWhite);
        `}
`;

const ArrowButton = styled(Button)`
  padding: 8px 12px;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  border: 1px solid var(--colorBlueGray900);
  border-radius: 4px;
  font-size: 18px;
  font-weight: bold;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  min-width: 42px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

interface Props extends CalendarProps {
  selectedDate: Date | Date[] | null;
  setSelectedDate: (date: Date | Date[] | null) => void;
  isDark?: boolean;
  showArrows?: boolean;
}

const DateInput: FC<Props> = ({
  selectedDate,
  setSelectedDate,
  isDark = false,
  showArrows = false,
  ...calendarProps
}) => {
  const { i18n } = useI18n();
  const { isShowing, toggle } = useModal();

  // Check if previous/next day should be disabled based on minDate/maxDate
  // If no date is selected, treat it as "today" for disabling arrows (but don't actually set it)
  const isPreviousDisabled = useMemo(() => {
    if (!calendarProps.minDate) {
      return false;
    }

    // Use selected date or fallback to today if none selected
    const currentDate = selectedDate instanceof Date ? selectedDate : new Date();
    const prevDay = new Date(currentDate);
    prevDay.setDate(prevDay.getDate() - 1);

    // Normalize dates to start of day for comparison
    const prevDayNormalized = new Date(
      prevDay.getFullYear(),
      prevDay.getMonth(),
      prevDay.getDate(),
    );
    const minDateNormalized = new Date(
      calendarProps.minDate.getFullYear(),
      calendarProps.minDate.getMonth(),
      calendarProps.minDate.getDate(),
    );

    return prevDayNormalized < minDateNormalized;
  }, [selectedDate, calendarProps.minDate]);

  const isNextDisabled = useMemo(() => {
    if (!calendarProps.maxDate) {
      return false;
    }

    // Use selected date or fallback to today if none selected
    const currentDate = selectedDate instanceof Date ? selectedDate : new Date();
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1);

    // Normalize dates to start of day for comparison
    const nextDayNormalized = new Date(
      nextDay.getFullYear(),
      nextDay.getMonth(),
      nextDay.getDate(),
    );
    const maxDateNormalized = new Date(
      calendarProps.maxDate.getFullYear(),
      calendarProps.maxDate.getMonth(),
      calendarProps.maxDate.getDate(),
    );

    return nextDayNormalized > maxDateNormalized;
  }, [selectedDate, calendarProps.maxDate]);

  // Arrow button handlers
  // If no date is selected, use today as the base date for calculations
  const goToPreviousDay = useButton({
    onClick: () => {
      const currentDate = selectedDate instanceof Date ? selectedDate : new Date();
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 1);
      setSelectedDate(newDate);
    },
    disabled: isPreviousDisabled,
  });

  const goToNextDay = useButton({
    onClick: () => {
      const currentDate = selectedDate instanceof Date ? selectedDate : new Date();
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 1);
      setSelectedDate(newDate);
    },
    disabled: isNextDisabled,
  });

  const Content = () => {
    const clearButton = useButton({
      onClick: () => {
        setSelectedDate(null);
        toggle();
      },
    });

    const OKButton = useButton({
      onClick: () => {
        toggle();
      },
    });

    const content = (
      <>
        <ReactCalendar
          {...calendarProps}
          onChange={(date) => {
            setSelectedDate(date);

            // If isRange and only one date is selected don't close the modal
            if (Array.isArray(date) && date.length === 1) {
              return;
            }

            toggle();
          }}
          value={selectedDate}
          maxDate={calendarProps.maxDate}
        />
        <Box marginTop="medium" display="flex" justifyContent="space-between">
          <Button {...clearButton}>{i18n('General.ClearDate')}</Button>
          {calendarProps.selectRange && (
            <BlueButton {...OKButton}>{i18n('General.Submit')}</BlueButton>
          )}
        </Box>
      </>
    );

    return isDark ? <DarkCalendar>{content}</DarkCalendar> : <div>{content}</div>;
  };

  const formatDateDisplay = (date: Date | Date[] | null) => {
    if (Array.isArray(date)) {
      return `${DateTime.fromJSDate(date[0]).toFormat('dd/MM/yyyy')} - ${DateTime.fromJSDate(
        date[1],
      ).toFormat('dd/MM/yyyy')}`;
    }

    if (date instanceof Date) {
      const d = DateTime.fromJSDate(date);
      const today = DateTime.local();
      const tomorrow = today.plus({ days: 1 });

      if (d.hasSame(today, 'day')) return i18n('General.Today');
      if (d.hasSame(tomorrow, 'day')) return i18n('General.Tomorrow');

      return d.toFormat('dd/MM/yyyy');
    }

    return i18n('General.NoDateSelected');
  };

  // Conditionally render with or without arrows
  if (showArrows) {
    return (
      <>
        <DateNavigationWrapper>
          <ArrowButton {...goToPreviousDay}>←</ArrowButton>
          <DateInputWrapper
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggle();
            }}
            activeDate={Boolean(selectedDate)}
            isDark={isDark}>
            <span>{formatDateDisplay(selectedDate)}</span>
            <Icon
              src={dateIcon}
              color={isDark ? 'var(--colorBlueGray200)' : 'var(--colorBlueGray1000)'}
            />
          </DateInputWrapper>
          <ArrowButton {...goToNextDay}>→</ArrowButton>
        </DateNavigationWrapper>
        {isShowing && (
          <Modal isShowing={true} hide={toggle}>
            <Content />
          </Modal>
        )}
      </>
    );
  }

  return (
    <>
      <DateInputWrapper
        onTouchEnd={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggle();
        }}
        activeDate={Boolean(selectedDate)}
        isDark={isDark}>
        <span>{formatDateDisplay(selectedDate)}</span>
        <Icon
          src={dateIcon}
          color={isDark ? 'var(--colorBlueGray200)' : 'var(--colorBlueGray1000)'}
        />
      </DateInputWrapper>
      {isShowing && (
        <Modal isShowing={true} hide={toggle}>
          <Content />
        </Modal>
      )}
    </>
  );
};

export default DateInput;
